/**
 * 고객 문의 — Heinrich (heinrich.dasancms.co.kr)
 */
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  FiMail,
  FiPhone,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiSend,
} from 'react-icons/fi'
import { api } from '../../lib/api'
import Card, { CardBody } from '../../components/ui/Card'
import Input from '../../components/ui/Input'
import Textarea from '../../components/ui/Textarea'
import Select from '../../components/ui/Select'
import Button from '../../components/ui/Button'
import { toast } from '../../hooks/useToast'
import { COMPANY } from '../../lib/company'
import { FAQ_ITEMS } from '../../data/faq'
import { renderWithBrand } from '../../components/brand/Brand'


/* ── FAQ 아이템 컴포넌트 ── */
function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-surface-elevated/50 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-surface-card/60 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-ink text-sm pr-4">{renderWithBrand(question)}</span>
        {open ? (
          <FiChevronUp className="shrink-0 text-brand-primary-400" size={16} />
        ) : (
          <FiChevronDown className="shrink-0 text-ink-muted" size={16} />
        )}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="faq-answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 pt-1 text-sm text-ink-muted leading-[1.8] border-t border-surface-elevated/40">
              {renderWithBrand(answer)}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ── 폼 스키마 ── */
const makeContactSchema = (t: (k: string) => string) => z.object({
  name: z.string().min(2, t('contact.nameMin')),
  email: z.string().email(t('contact.emailInvalid')),
  phone: z.string().optional(),
  type: z.string().min(1, t('contact.typeRequired')),
  message: z.string().min(10, t('contact.messageMin')).max(2000, t('contact.messageMax')),
})

type ContactFormData = z.infer<ReturnType<typeof makeContactSchema>>

/* ── 메인 컴포넌트 ── */
export default function Contact() {
  const { t } = useTranslation()

  const INQUIRY_TYPES = [
    { value: 'general', label: t('contact.inquiryTypes.general') },
    { value: 'sales', label: t('contact.inquiryTypes.sales') },
    { value: 'technical', label: t('contact.inquiryTypes.technical') },
    { value: 'billing', label: t('contact.inquiryTypes.billing') },
    { value: 'partnership', label: t('contact.inquiryTypes.partnership') },
    { value: 'privacy', label: t('contact.inquiryTypes.privacy') },
    { value: 'other', label: t('contact.inquiryTypes.other') },
  ]

  const CONTACT_CARDS = [
    // 도입·기술·결제·파트너십 등 모든 문의를 단일 이메일로 통합 (중복 카드 제거)
    { icon: FiMail, title: t('contact.cardGeneral'), content: COMPANY.email, href: `mailto:${COMPANY.email}`, sub: t('contact.subEmailReply'), color: 'text-brand-primary-400', bg: 'bg-brand-primary/10' },
    { icon: FiClock, title: t('contact.cardSla'), content: t('contact.slaValue'), href: undefined, sub: t('contact.slaTime'), color: 'text-brand-secondary-400', bg: 'bg-brand-secondary/10' },
    { icon: FiPhone, title: t('contact.cardPhone'), content: COMPANY.phone, href: (COMPANY.phone as string).startsWith('{{') ? undefined : `tel:${COMPANY.phone}`, sub: t('contact.phoneTime'), color: 'text-purple-400', bg: 'bg-purple-400/10' },
  ]

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(makeContactSchema(t)),
    defaultValues: { type: '' },
  })

  const onSubmit = async (data: ContactFormData) => {
    try {
      await api.post('/support/contact', data)
      toast.success(t('contact.successMessage'))
      reset()
    } catch {
      toast.error(t('contact.sendFailed'))
    }
  }

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <title>{t('contact.pageTitle', { name: COMPANY.service_name })}</title>
        <meta name="description" content={t('contact.ogDesc')} />
        <link rel="canonical" href={`https://${COMPANY.domain}/contact`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Heinrich" />
        <meta property="og:url" content={`https://${COMPANY.domain}/contact`} />
        <meta property="og:title" content={t('contact.pageTitle', { name: COMPANY.service_name })} />
        <meta property="og:description" content={t('contact.ogDesc')} />
        <meta property="og:image" content={`https://${COMPANY.domain}/main-logo.png`} />
      </Helmet>

      {/* ── Hero ── */}
      <section className="bg-surface-deep pt-28 pb-16 border-b border-surface-elevated/30">
        <div className="container-custom max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <span className="inline-block text-xs font-bold text-brand-primary-400 uppercase tracking-widest mb-3">
              {t('contact.supportBadge')}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-ink mb-4">
              {t('contact.heroTitle')}
            </h1>
            <p className="text-ink-muted text-lg max-w-2xl leading-[1.75]">
              {t('contact.subtitleFull')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── 연락처 카드 그리드 ── */}
      <section className="py-14 bg-surface">
        <div className="container-custom max-w-5xl">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {CONTACT_CARDS.map(({ icon: Icon, title, content, href, sub, color, bg }, idx) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.07 }}
              >
                <Card hover className="h-full">
                  <CardBody className="flex flex-col gap-3">
                    <div
                      className={`h-10 w-10 rounded-md ${bg} flex items-center justify-center`}
                    >
                      <Icon size={18} className={color} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-ink-faint uppercase tracking-wider mb-1">
                        {title}
                      </p>
                      {href ? (
                        <a href={href} className={`text-sm font-semibold ${color} hover:underline`}>
                          {content}
                        </a>
                      ) : (
                        <p className={`text-sm font-semibold ${color}`}>{content}</p>
                      )}
                      <p className="text-xs text-ink-faint mt-1">{sub}</p>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 문의 폼 ── */}
      <section className="py-16 bg-surface-deep">
        <div className="container-custom max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* 좌측 안내 */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-6"
            >
              <div>
                <h2 className="text-2xl font-bold text-ink mb-3">{t('contact.sectionTitle')}</h2>
                <p className="text-ink-muted text-sm leading-[1.8]">
                  {t('contact.sectionDesc', { email: '' }).replace('{{email}}', '')}
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="text-brand-primary-400 underline underline-offset-2"
                  >
                    {COMPANY.email}
                  </a>
                </p>
              </div>

              <div className="space-y-3">
                {[
                  { q: t('contact.examples.free'), desc: t('contact.examples.freeDesc') },
                  { q: t('contact.examples.enterprise'), desc: t('contact.examples.enterpriseDesc') },
                  { q: t('contact.examples.partnership'), desc: t('contact.examples.partnershipDesc') },
                ].map(({ q, desc }) => (
                  <div key={q} className="flex items-start gap-2 text-sm">
                    <span className="text-brand-primary-400 font-bold mt-0.5">•</span>
                    <div>
                      <span className="text-ink font-semibold">{q}</span>
                      <span className="text-ink-muted ml-2">{desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* 지도 자리 */}
              <div className="rounded-md border border-surface-elevated/40 overflow-hidden h-40 bg-surface-card flex items-center justify-center">
                {/* TODO: {{MAP_EMBED}} — Google Maps 또는 Kakao Map iframe으로 교체 */}
                <div className="text-center text-ink-faint text-xs">
                  <FiPhone size={20} className="mx-auto mb-1 opacity-40" />
                  <span>{t('contact.mapPlaceholder')}</span>
                  <br />
                  <span className="text-[11px] opacity-60">{COMPANY.address}</span>
                </div>
              </div>
            </motion.div>

            {/* 폼 */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-3"
            >
              <Card>
                <CardBody>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-5"
                    noValidate
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Input
                        label={t('contact.formName')}
                        required
                        placeholder={t('auth.namePlaceholder')}
                        autoComplete="name"
                        error={errors.name?.message}
                        {...register('name')}
                      />
                      <Input
                        label={t('contact.formEmail')}
                        type="email"
                        required
                        placeholder={t('auth.emailPlaceholder')}
                        autoComplete="email"
                        error={errors.email?.message}
                        {...register('email')}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Input
                        label={t('contact.formPhone')}
                        type="tel"
                        placeholder={t('contact.phonePlaceholder')}
                        autoComplete="tel"
                        hint={t('contact.phoneHint')}
                        error={errors.phone?.message}
                        {...register('phone')}
                      />
                      <Select
                        label={t('contact.formType')}
                        required
                        placeholder={t('contact.typePlaceholder')}
                        options={INQUIRY_TYPES}
                        error={errors.type?.message}
                        {...register('type')}
                      />
                    </div>

                    <Textarea
                      label={t('contact.formMessage')}
                      required
                      placeholder={t('contact.messagePlaceholder')}
                      rows={6}
                      error={errors.message?.message}
                      {...register('message')}
                    />

                    <p className="text-xs text-ink-faint">
                      {t('contact.privacyNote')}
                    </p>

                    <Button
                      type="submit"
                      size="lg"
                      isLoading={isSubmitting}
                      leftIcon={<FiSend size={16} />}
                      className="w-full"
                    >
                      {t('contact.formSubmit')}
                    </Button>
                  </form>
                </CardBody>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-16 bg-surface scroll-mt-20">
        <div className="container-custom max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs font-bold text-brand-primary-400 uppercase tracking-widest">
              {t('contact.faqBadge')}
            </span>
            <h2 className="text-3xl font-bold text-ink mt-3 mb-4">{t('contact.faqTitle')}</h2>
            <p className="text-ink-muted">{t('contact.faqSubtitle')}</p>
          </motion.div>

          <div className="space-y-3">
            {FAQ_ITEMS.map(({ id, question, answer }) => (
              <motion.div
                key={id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <FaqItem question={question} answer={answer} />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <p className="text-ink-muted text-sm mb-2">{t('contact.faqNotFound')}</p>
            <a
              href={`mailto:${COMPANY.email}`}
              className="inline-flex items-center gap-2 text-brand-primary-400 font-semibold hover:underline text-sm"
            >
              <FiMail size={14} />
              {COMPANY.email}
            </a>
          </motion.div>
        </div>
      </section>
    </>
  )
}
